import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Typography,
    Chip,
    Grid2,
    IconButton,
    Alert,
    LinearProgress,
    Tooltip
} from '@mui/material';
import {
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Security as SecurityIcon,
    Monitor as MonitoringIcon
} from '@mui/icons-material';
import { useRPCHealthMonitor } from '../hooks/useMultiProviderRPC';

interface ProviderStatus {
    status: 'healthy' | 'unhealthy' | 'unknown';
    lastSuccess?: number;
    lastFailure?: number;
    responseTime?: number;
    consecutiveFailures: number;
    lastError?: string;
}

interface RPCHealthMonitorProps {
    compact?: boolean;
    showDetails?: boolean;
}

interface RPCStatusIndicatorProps { }

interface RPCHealthAlertProps { }

interface RPCHealthProgressProps {
    showLabel?: boolean;
}

/**
 * RPC Health Monitor Component (Material-UI)
 * Displays real-time health status of all RPC providers
 */
export const RPCHealthMonitor: React.FC<RPCHealthMonitorProps> = ({ compact = false, showDetails = true }) => {
    const { healthData, getHealthySummary, isLoading, error } = useRPCHealthMonitor();
    const [isExpanded, setIsExpanded] = useState(!compact);

    const summary = getHealthySummary();

    // Show loading state
    if (isLoading && Object.keys(healthData.providers).length === 0) {
        return (
            <Card>
                <CardHeader title="RPC Provider Health" />
                <CardContent>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                        <LinearProgress sx={{ flexGrow: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                            Loading health data...
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    // Show error state with fallback
    if (error && Object.keys(healthData.providers).length === 0) {
        return (
            <Card>
                <CardHeader title="RPC Provider Health" />
                <CardContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight="medium">
                            Health Monitoring Unavailable
                        </Typography>
                        <Typography variant="body2">
                            Backend health API error: {error}. Using fallback monitoring.
                        </Typography>
                    </Alert>
                </CardContent>
            </Card>
        );
    }

    const getStatusColor = (status: string): 'success' | 'error' | 'default' => {
        switch (status) {
            case 'healthy':
                return 'success';
            case 'unhealthy':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'healthy':
                return <CheckCircleIcon color="success" />;
            case 'unhealthy':
                return <ErrorIcon color="error" />;
            default:
                return <WarningIcon color="warning" />;
        }
    };

    const formatResponseTime = (time?: number): string => {
        if (!time) return 'N/A';
        return `${time}ms`;
    };

    const formatTimestamp = (timestamp?: number): string => {
        if (!timestamp) return 'Never';
        return new Date(timestamp).toLocaleTimeString();
    };

    const getSeverityColor = (percentage: number): 'success' | 'warning' | 'error' => {
        if (percentage >= 80) return 'success';
        if (percentage >= 50) return 'warning';
        return 'error';
    };

    // Filter providers by type for three-section layout
    const filterProvidersByType = (type: 'mev' | 'node' | 'dev') => {
        const filtered = Object.entries(healthData.providers).filter(([providerName, statusData]) => {
            const status = statusData as ProviderStatus & { type?: string };
            // Use backend-provided type data (no hardcoded fallbacks)
            return status.type === type;
        });

        return filtered;
    };

    const mevProviders = filterProvidersByType('mev');
    const nodeProviders = filterProvidersByType('node');
    const devProviders = filterProvidersByType('dev');

    const getMevSummary = () => {
        const healthy = mevProviders.filter(([, status]) => (status as ProviderStatus).status === 'healthy').length;
        const total = mevProviders.length;
        return { healthy, total, percentage: total > 0 ? Math.round((healthy / total) * 100) : 0 };
    };

    const getNodeSummary = () => {
        const healthy = nodeProviders.filter(([, status]) => (status as ProviderStatus).status === 'healthy').length;
        const total = nodeProviders.length;
        return { healthy, total, percentage: total > 0 ? Math.round((healthy / total) * 100) : 0 };
    };

    const getDevSummary = () => {
        const healthy = devProviders.filter(([, status]) => (status as ProviderStatus).status === 'healthy').length;
        const total = devProviders.length;
        return { healthy, total, percentage: total > 0 ? Math.round((healthy / total) * 100) : 0 };
    };

    const mevSummary = getMevSummary();
    const nodeSummary = getNodeSummary();
    const devSummary = getDevSummary();

    if (compact && !isExpanded) {
        return (
            <Card
                sx={{
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 2 },
                    transition: 'box-shadow 0.2s'
                }}
                onClick={() => setIsExpanded(true)}
            >
                <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="body2" fontWeight="medium">
                            RPC:
                        </Typography>
                        <Chip
                            label={`${summary.healthy}/${summary.total}`}
                            color={getSeverityColor(summary.healthPercentage)}
                            size="small"
                        />
                        <Typography variant="caption" color="text.secondary">
                            {summary.healthPercentage}% healthy
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader
                title="RPC Provider Health"
                action={
                    compact && (
                        <IconButton onClick={() => setIsExpanded(false)}>
                            <CloseIcon />
                        </IconButton>
                    )
                }
                subheader={
                    <Box display="flex" alignItems="center" gap={2} mt={1}>
                        <Chip
                            label={`${summary.healthy}/${summary.total} Healthy (${summary.healthPercentage}%)`}
                            color={getSeverityColor(summary.healthPercentage)}
                            size="small"
                        />
                        {healthData.lastUpdate && (
                            <Typography variant="caption" color="text.secondary">
                                Last updated: {healthData.lastUpdate.toLocaleTimeString()}
                            </Typography>
                        )}
                    </Box>
                }
            />

            {showDetails && (
                <CardContent>
                    {/* Two-Column Layout: MEV and NODE Providers */}
                    <Grid2 container spacing={3} mb={3}>
                        {/* MEV RPC Health Column */}
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(25, 118, 210, 0.02)' }}>
                                <CardHeader
                                    title={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <SecurityIcon color="primary" />
                                            <Typography variant="h6" fontWeight="bold">
                                                MEV RPC Health
                                            </Typography>
                                        </Box>
                                    }
                                    subheader="Transaction Protection"
                                    action={
                                        <Chip
                                            label={`${mevSummary.healthy}/${mevSummary.total} (${mevSummary.percentage}%)`}
                                            color={getSeverityColor(mevSummary.percentage)}
                                            size="small"
                                        />
                                    }
                                />
                                <CardContent>
                                    {mevProviders.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                            No MEV providers configured
                                        </Typography>
                                    ) : (
                                        mevProviders.map(([providerName, statusData]) => {
                                            const status = statusData as ProviderStatus;
                                            return (
                                                <Card key={providerName} variant="outlined" sx={{ mb: 2 }}>
                                                    <CardContent sx={{ py: 2 }}>
                                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                                            <Box display="flex" alignItems="center" gap={2}>
                                                                {getStatusIcon(status.status)}
                                                                <Box>
                                                                    <Typography variant="subtitle2" fontWeight="medium">
                                                                        {(status as any).displayName || providerName}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {status.status === 'healthy' ? 'Operational' : 'Issues detected'}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                            <Box textAlign="right">
                                                                <Chip
                                                                    label={status.status}
                                                                    color={getStatusColor(status.status)}
                                                                    size="small"
                                                                />
                                                                <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                                                                    {formatResponseTime(status.responseTime)}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            );
                                        })
                                    )}
                                </CardContent>
                            </Card>
                        </Grid2>

                        {/* DEV RPC Health Container (Below MEV, only shown in dev/fork mode) */}
                        {devProviders.length > 0 && (
                            <Grid2 size={{ xs: 12, md: 6 }}>
                                <Card variant="outlined" sx={{ bgcolor: 'rgba(255, 152, 0, 0.02)', border: '2px dashed rgba(255, 152, 0, 0.3)' }}>
                                    <CardHeader
                                        title={
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Box
                                                    sx={{
                                                        width: 24,
                                                        height: 24,
                                                        borderRadius: '50%',
                                                        bgcolor: 'warning.main',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    🔧
                                                </Box>
                                                <Typography variant="h6" fontWeight="bold" color="warning.main">
                                                    DEV RPC Health
                                                </Typography>
                                            </Box>
                                        }
                                        subheader={
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Development Mode
                                                </Typography>
                                                <Chip label="Local/Fork" size="small" color="warning" variant="outlined" />
                                            </Box>
                                        }
                                        action={
                                            <Chip
                                                label={`${devSummary.healthy}/${devSummary.total} (${devSummary.percentage}%)`}
                                                color={getSeverityColor(devSummary.percentage)}
                                                size="small"
                                            />
                                        }
                                    />
                                    <CardContent>
                                        {devProviders.map(([providerName, statusData]) => {
                                            const status = statusData as ProviderStatus;
                                            return (
                                                <Card key={providerName} variant="outlined" sx={{ mb: 2 }}>
                                                    <CardContent sx={{ py: 2 }}>
                                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                                            <Box display="flex" alignItems="center" gap={2}>
                                                                {getStatusIcon(status.status)}
                                                                <Box>
                                                                    <Typography variant="subtitle2" fontWeight="medium">
                                                                        {(status as any).displayName || providerName}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {status.status === 'healthy' ? 'Operational' : 'Issues detected'}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                            <Box textAlign="right">
                                                                <Chip
                                                                    label={status.status}
                                                                    color={getStatusColor(status.status)}
                                                                    size="small"
                                                                />
                                                                <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                                                                    {formatResponseTime(status.responseTime)}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            );
                                        })}
                                    </CardContent>
                                </Card>
                            </Grid2>
                        )}

                        {/* NODE RPC Health Column */}
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(76, 175, 80, 0.02)' }}>
                                <CardHeader
                                    title={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <MonitoringIcon color="success" />
                                            <Typography variant="h6" fontWeight="bold">
                                                NODE RPC Health
                                            </Typography>
                                        </Box>
                                    }
                                    subheader="Network Monitoring"
                                    action={
                                        <Chip
                                            label={`${nodeSummary.healthy}/${nodeSummary.total} (${nodeSummary.percentage}%)`}
                                            color={getSeverityColor(nodeSummary.percentage)}
                                            size="small"
                                        />
                                    }
                                />
                                <CardContent>
                                    {nodeProviders.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                            No NODE providers configured
                                        </Typography>
                                    ) : (
                                        nodeProviders.map(([providerName, statusData]) => {
                                            const status = statusData as ProviderStatus;
                                            return (
                                                <Card key={providerName} variant="outlined" sx={{ mb: 2 }}>
                                                    <CardContent sx={{ py: 2 }}>
                                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                                            <Box display="flex" alignItems="center" gap={2}>
                                                                {getStatusIcon(status.status)}
                                                                <Box>
                                                                    <Typography variant="subtitle2" fontWeight="medium">
                                                                        {(status as any).displayName || providerName}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {status.status === 'healthy' ? 'Operational' : 'Issues detected'}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                            <Box textAlign="right">
                                                                <Chip
                                                                    label={status.status}
                                                                    color={getStatusColor(status.status)}
                                                                    size="small"
                                                                />
                                                                <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                                                                    {formatResponseTime(status.responseTime)}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            );
                                        })
                                    )}
                                </CardContent>
                            </Card>
                        </Grid2>
                    </Grid2>

                    {/* Summary Statistics */}
                    <Grid2 container spacing={2} textAlign="center">
                        <Grid2 size={{ xs: 6, md: 3 }}>
                            <Typography variant="h4" color="success.main" fontWeight="bold">
                                {summary.healthy}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Total Healthy
                            </Typography>
                        </Grid2>
                        <Grid2 size={{ xs: 6, md: 3 }}>
                            <Typography variant="h4" color="error.main" fontWeight="bold">
                                {summary.unhealthy}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Total Unhealthy
                            </Typography>
                        </Grid2>
                        <Grid2 size={{ xs: 6, md: 3 }}>
                            <Typography variant="h4" color="primary.main" fontWeight="bold">
                                {mevSummary.healthy}/{mevSummary.total}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                MEV Healthy
                            </Typography>
                        </Grid2>
                        <Grid2 size={{ xs: 6, md: 3 }}>
                            <Typography variant="h4" color="success.main" fontWeight="bold">
                                {nodeSummary.healthy}/{nodeSummary.total}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                NODE Healthy
                            </Typography>
                        </Grid2>
                    </Grid2>
                </CardContent>
            )}
        </Card>
    );
};

/**
 * Simple RPC Status Indicator for header/navbar
 */
export const RPCStatusIndicator: React.FC<RPCStatusIndicatorProps> = () => {
    const { getHealthySummary } = useRPCHealthMonitor();
    const summary = getHealthySummary();

    const getIndicatorColor = (): string => {
        if (summary.healthPercentage >= 80) return 'success.main';
        if (summary.healthPercentage >= 50) return 'warning.main';
        return 'error.main';
    };

    return (
        <Box display="flex" alignItems="center" gap={1}>
            <Box
                sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: getIndicatorColor()
                }}
            />
            <Typography variant="body2" color="text.secondary">
                RPC: {summary.healthy}/{summary.total}
            </Typography>
        </Box>
    );
};

/**
 * RPC Health Alert Component
 * Shows warnings when providers are unhealthy
 */
export const RPCHealthAlert: React.FC<RPCHealthAlertProps> = () => {
    const { getHealthySummary } = useRPCHealthMonitor();
    const [isDismissed, setIsDismissed] = useState(false);
    const summary = getHealthySummary();

    if (isDismissed || summary.healthPercentage >= 80) {
        return null;
    }

    const getAlertSeverity = (): 'error' | 'warning' => {
        if (summary.healthPercentage === 0) return 'error';
        if (summary.healthPercentage < 50) return 'error';
        return 'warning';
    };

    const getAlertMessage = (): string => {
        if (summary.healthPercentage === 0) {
            return 'All RPC providers are currently unavailable. Please check your connection.';
        }
        if (summary.healthPercentage < 50) {
            return `Most RPC providers are experiencing issues (${summary.healthy}/${summary.total} healthy). Some features may be slow.`;
        }
        return `Some RPC providers are experiencing issues (${summary.healthy}/${summary.total} healthy). Automatic failover is active.`;
    };

    return (
        <Alert
            severity={getAlertSeverity()}
            sx={{ mb: 2 }}
            action={
                <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => setIsDismissed(true)}
                >
                    <CloseIcon fontSize="inherit" />
                </IconButton>
            }
        >
            <Typography variant="subtitle2" fontWeight="medium">
                RPC Provider Issues Detected
            </Typography>
            <Typography variant="body2">
                {getAlertMessage()}
            </Typography>
        </Alert>
    );
};

/**
 * RPC Health Progress Bar Component
 * Shows health percentage as a progress bar
 */
export const RPCHealthProgress: React.FC<RPCHealthProgressProps> = ({ showLabel = true }) => {
    const { getHealthySummary } = useRPCHealthMonitor();
    const summary = getHealthySummary();

    const getProgressColor = (): 'success' | 'warning' | 'error' => {
        if (summary.healthPercentage >= 80) return 'success';
        if (summary.healthPercentage >= 50) return 'warning';
        return 'error';
    };

    return (
        <Box>
            {showLabel && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">RPC Health</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {summary.healthy}/{summary.total} ({summary.healthPercentage}%)
                    </Typography>
                </Box>
            )}
            <LinearProgress
                variant="determinate"
                value={summary.healthPercentage}
                color={getProgressColor()}
                sx={{ height: 8, borderRadius: 4 }}
            />
        </Box>
    );
};

export default RPCHealthMonitor;
